import React, { useCallback, useMemo } from 'react';
import { Grid, Typography } from '@material-ui/core';

// Types
import { DisplayItem, CreditCardData, InputDescription } from '../../../types';

// Icons
import { paguelofacil } from '../../../svg';

// Functions
import {
  cardTypeIcon,
  creditCardTypeForNumber,
  shouldShowSecurityCode,
} from '../../../functions/helpers';

// Components
import { TextField, SearchableDropDown } from '../../Inputs';

// Const
import { AMEX } from '../../../constants/cardTypes';
import NetworkIndicator from '../../General/NetworkIndicator';

export type InputIds = {
  cardName?: string;
  cardNumber?: string;
  expirationDate?: string;
  securityCode?: string;
  bankEntityCode?: string;
  cardAddress?: string;
};

type UpdateParams = { [P in keyof CreditCardData]?: string };

export type CreditCardFormProps = {
  /** Current values fo the field */
  creditCard: CreditCardData;
  /** The list of banks that will be displayed */
  bankList: DisplayItem[];
  /** Called when any of the values change, will report the entire object and the single field that change */
  onChange: (newData: CreditCardData, updatedParam: UpdateParams) => void;
  /** Errors reported for each input */
  errors: { [key: string]: string };
  /** If true, the bank list will be replaced by a loading element */
  requestingBanks?: boolean;
  /** Provided input ids for the automated tests */
  inputIds?: InputIds;
  /** If true hidden the security code input */
  isHiddenSecurityCodeInput?: boolean;
  /**
   * If we pass a reset props, use it to clear the security code input
   * else clear the security input with getInputProps.
   * We need to clear the input with reset when the validation is done
   * onChange because if we set the value with getInputProps it dispatch
   * the validation on security code input
   */
  reset?: (fieldNames: string[]) => void;
  /**
   * Hide card adress if client address exists
   */
  clientAddress?: string;
};

enum fieldCardNames {
  cardName = 'cardName',
  cardNumber = 'cardNumber',
  expirationDate = 'expirationDate',
  securityCode = 'securityCode',
  cardAddress = 'cardAddress',
}

const CreditCardForm = ({
  creditCard,
  bankList,
  onChange,
  errors,
  requestingBanks = false,
  inputIds = {},
  isHiddenSecurityCodeInput = false,
  reset,
  clientAddress = '',
}: CreditCardFormProps): JSX.Element => {
  /**
   * Prevents the user from typing invalid values on the credit card field
   */
  const shouldUpdateCreditCardName = useCallback(
    (newValue: string): boolean => {
      return newValue === '' || /^[a-zA-Z\s]*$/.exec(newValue) !== null;
    },
    [],
  );

  /**
   * Prevents the user from typing invalid values on the security code field
   */
  const shouldUpdateSecurityCode = useCallback((newValue: string): boolean => {
    return (
      newValue === '' ||
      (/[0-9]+/.exec(newValue) !== null && newValue.length <= 3)
    );
  }, []);

  /**
   * This method is called when a value changes, and will update the complete form shape
   * to whatever is in charge of managing its values.
   */

  const updateNewCreditCardValue = useCallback(
    (params: UpdateParams): void => {
      const newCreditCard: CreditCardData = {
        ...creditCard,
        ...params,
      };
      onChange(newCreditCard, params);
    },
    [creditCard, onChange],
  );

  /**
   * Attempts to extract an error from the provided error dictionary for the given field.
   */
  const getError = useCallback(
    (inputName: string): string => {
      if (Object.keys(errors).includes(inputName)) {
        return errors[inputName];
      }
      return '';
    },
    [errors],
  );

  const showSecurityCode = useMemo(() => {
    if (isHiddenSecurityCodeInput) {
      return false;
    }
    // Clean security code when the user enter other type of credit card
    if (reset) {
      reset([fieldCardNames.securityCode]);
    } else {
      updateNewCreditCardValue({ [fieldCardNames.securityCode]: '' });
    }
    return shouldShowSecurityCode(creditCard.cardType);
  }, [creditCard.cardType]);

  const creditCardIcon = useMemo(() => cardTypeIcon(creditCard.cardType), [
    creditCard.cardType,
  ]);

  /**
   * The bank field can dynamically show or hide, depending on the request status
   */
  const bankField = useMemo(() => {
    const field = (
      <>
        <SearchableDropDown
          inputId={inputIds.bankEntityCode}
          inputName="bankEntityCode"
          inputLabel="Seleccione la entidad bancaria"
          options={bankList}
          defaultValue={creditCard.bankEntityCode}
          onChange={(newVal): void =>
            updateNewCreditCardValue({ bankEntityCode: newVal.Value })
          }
          inputError={getError('bankEntityCode') !== ''}
          inputErrorMessage={getError('bankEntityCode')}
        />
        <Typography variant="caption">
          Si tu entidad bancaria no está en este listado, selecciona &quot;Otros
          bancos extranjeros&quot;
        </Typography>
      </>
    );
    return (
      <>
        <NetworkIndicator
          data-testid="bank-network-indicator"
          variant="horizontal"
          message="Obteniendo bancos..."
          requesting={requestingBanks}
        />
        {!requestingBanks && field}
      </>
    );
  }, [requestingBanks, bankList, creditCard]);

  /**
   * Generates common properties for all inputs, depending on the provided name
   */
  const makeInputProps = useCallback(
    (forName: string): InputDescription<string> => {
      return {
        inputName: forName,
        defaultValue: creditCard[forName],
        onChange: (value): void =>
          updateNewCreditCardValue({ [forName]: value }),
        inputError: getError(forName) !== '',
        inputErrorMessage: getError(forName),
      };
    },
    [creditCard, updateNewCreditCardValue, getError],
  );

  return (
    <Grid container spacing={2}>
      {/* The name of the card holder */}
      <Grid item xs={12} md={6}>
        <TextField
          {...makeInputProps(fieldCardNames.cardName)}
          inputId={inputIds.cardName}
          inputLabel="Nombre en la tarjeta"
          shouldUpdate={shouldUpdateCreditCardName}
        />
      </Grid>

      {/* Credit card number */}
      <Grid item xs={12} md={6}>
        <TextField
          {...makeInputProps(fieldCardNames.cardNumber)}
          inputId={inputIds.cardNumber}
          inputLabel="Número de la tarjeta"
          maskOptions={{
            creditCard: true,
          }}
          onChange={(newVal: string): void => {
            updateNewCreditCardValue({
              cardNumber: newVal,
              cardType: creditCardTypeForNumber(newVal),
            });
          }}
          customDecoration={creditCardIcon}
        />
      </Grid>

      {/* Expiration date */}
      <Grid item xs={6}>
        <TextField
          {...makeInputProps(fieldCardNames.expirationDate)}
          inputId={inputIds.expirationDate}
          inputLabel="Fecha de expiración"
          maskOptions={{
            date: true,
            datePattern: ['m', 'y'],
          }}
        />
      </Grid>

      {/* Security Code */}
      {/* Hidden when card type is amex */}
      {showSecurityCode && (
        <Grid item xs={6}>
          <TextField
            {...makeInputProps(fieldCardNames.securityCode)}
            inputId={inputIds.securityCode}
            inputLabel="Código de seguridad"
            shouldUpdate={shouldUpdateSecurityCode}
          />
        </Grid>
      )}

      {/* The card holder's bank */}
      <Grid item xs={12} md={6}>
        {bankField}
      </Grid>

      {/* Address */}
      {clientAddress === '' && (
        <Grid item xs={12} md={6}>
          <TextField
            {...makeInputProps(fieldCardNames.cardAddress)}
            inputId={inputIds.cardAddress}
            inputLabel="Dirección residencial"
          />
        </Grid>
      )}

      {/* Payment processor logo */}
      {/* Hidden when card type is amex */}
      {creditCard.cardType !== AMEX && (
        <Grid item xs={12} container justifyContent="center">
          <img
            alt="paguelofacil"
            src={paguelofacil}
            data-testid="paguelo-facil-image"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default CreditCardForm;
